import {
  CanvasTexture,
  DoubleSide,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  PlaneGeometry,
  SRGBColorSpace,
  Vector3,
} from 'three'
import type { Pilot } from '../physics/Pilot'
import type { FirstFlight } from '../game/firstFlight'

const W = 512
const H = 288
const WIDTH_M = 0.52
const HEIGHT_M = (WIDTH_M * H) / W

/**
 * Head-locked instrument panel (DESIGN.md §3 "the gravity arrow can graduate
 * into a diegetic HUD element (helmet attitude indicator)").
 *
 * This is not decoration. §2a is explicit that the pilot has no proprioceptive
 * access to virtual-down — their inner ear reports *room* vertical while the
 * game demands alignment to *world* vertical — so attitude has to come in
 * entirely through the eyes. Flying without an attitude reference is the
 * helicopter-pilot problem with the instruments removed.
 *
 * Head-locked is also the §8 comfort mitigation #3 (a fixed cockpit reference
 * frame), so this earns its place twice.
 */
export class VrHud {
  readonly mesh: Mesh

  private readonly canvas = document.createElement('canvas')
  private readonly ctx: CanvasRenderingContext2D
  private readonly texture: CanvasTexture
  private lastDraw = 0

  constructor() {
    this.canvas.width = W
    this.canvas.height = H
    this.ctx = this.canvas.getContext('2d')!
    this.texture = new CanvasTexture(this.canvas)
    this.texture.colorSpace = SRGBColorSpace

    this.mesh = new Mesh(
      new PlaneGeometry(WIDTH_M, HEIGHT_M),
      new MeshBasicMaterial({
        map: this.texture,
        transparent: true,
        side: DoubleSide,
        depthTest: false,
        fog: false,
      }),
    )
    // Just below the eye line, at a comfortable focal distance. `renderOrder`
    // plus depthTest:false keeps it readable against a building at any range.
    this.mesh.position.set(0, -0.2, -0.95)
    this.mesh.renderOrder = 999
    this.mesh.visible = false
  }

  /** Parent to the camera so it rides the head, not the body. */
  attach(camera: Object3D): void {
    camera.add(this.mesh)
  }

  setVisible(visible: boolean): void {
    this.mesh.visible = visible
  }

  update(pilot: Pilot, flight: FirstFlight, time: number, extra: { preset: string; fps: number }): void {
    if (!this.mesh.visible) return
    // 20 Hz is plenty for numbers and keeps the canvas upload off the frame
    // budget; the physics still runs at 90.
    if (time - this.lastDraw < 0.05) return
    this.lastDraw = time
    this.draw(pilot, flight, extra)
  }

  private draw(pilot: Pilot, flight: FirstFlight, extra: { preset: string; fps: number }): void {
    const ctx = this.ctx
    ctx.clearRect(0, 0, W, H)

    this.drawAttitude(ctx, pilot)
    this.drawThrottles(ctx, pilot)
    this.drawNumbers(ctx, pilot, flight, extra)
    this.drawPosture(ctx, pilot)

    this.texture.needsUpdate = true
  }

  /**
   * Artificial horizon driven by the *body's* attitude, not the head's. The
   * head is free to look around; what the player needs to know is which way the
   * simulated body is tipped, because that is what the thrust vector rotates
   * with.
   */
  private drawAttitude(ctx: CanvasRenderingContext2D, pilot: Pilot): void {
    const cx = W / 2
    const cy = 118
    const r = 76

    _up.set(0, 1, 0).applyQuaternion(pilot.body.orientation)
    _fwd.set(0, 0, -1).applyQuaternion(pilot.body.orientation)
    _right.set(1, 0, 0).applyQuaternion(pilot.body.orientation)

    const pitch = Math.asin(Math.max(-1, Math.min(1, -_fwd.y)))
    const roll = Math.atan2(_right.y, _up.y)
    const tilt = pilot.tiltDegrees()

    ctx.save()
    ctx.beginPath()
    ctx.arc(cx, cy, r, 0, Math.PI * 2)
    ctx.clip()

    ctx.translate(cx, cy)
    ctx.rotate(roll)
    const pitchPx = (pitch / (Math.PI / 2)) * r

    ctx.fillStyle = 'rgba(40, 78, 128, 0.85)'
    ctx.fillRect(-r * 2, -r * 2 + pitchPx, r * 4, r * 2)
    ctx.fillStyle = 'rgba(96, 62, 34, 0.85)'
    ctx.fillRect(-r * 2, pitchPx, r * 4, r * 2)

    ctx.strokeStyle = '#e8f2ff'
    ctx.lineWidth = 3
    ctx.beginPath()
    ctx.moveTo(-r * 1.4, pitchPx)
    ctx.lineTo(r * 1.4, pitchPx)
    ctx.stroke()

    // Pitch ladder every 30 deg, so large attitudes stay readable.
    ctx.strokeStyle = 'rgba(232, 242, 255, 0.5)'
    ctx.lineWidth = 2
    for (const deg of [-60, -30, 30, 60]) {
      const y = pitchPx - (deg / 90) * r
      ctx.beginPath()
      ctx.moveTo(-26, y)
      ctx.lineTo(26, y)
      ctx.stroke()
    }
    ctx.restore()

    // Fixed aircraft reference — the thing the horizon moves relative to.
    ctx.strokeStyle = tilt > 60 ? '#ff6b6b' : tilt > 25 ? '#ffb454' : '#7ee787'
    ctx.lineWidth = 4
    ctx.beginPath()
    ctx.moveTo(cx - 34, cy)
    ctx.lineTo(cx - 12, cy)
    ctx.moveTo(cx + 12, cy)
    ctx.lineTo(cx + 34, cy)
    ctx.moveTo(cx, cy - 8)
    ctx.lineTo(cx, cy + 4)
    ctx.stroke()

    ctx.strokeStyle = 'rgba(200, 220, 245, 0.55)'
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.arc(cx, cy, r, 0, Math.PI * 2)
    ctx.stroke()
  }

  /** Per-rocket throttle, with a hover reference so you can find the trim. */
  private drawThrottles(ctx: CanvasRenderingContext2D, pilot: Pilot): void {
    const hover = pilot.hoverThrottle
    const barH = 150
    const top = 42

    for (let i = 0; i < 2; i++) {
      const rocket = pilot.debug.rockets[i]
      const x = i === 0 ? 44 : W - 44 - 26

      ctx.fillStyle = 'rgba(20, 28, 40, 0.75)'
      ctx.fillRect(x, top, 26, barH)

      const fill = Math.max(0, Math.min(1, rocket.level)) * barH
      ctx.fillStyle = rocket.tracked ? (i === 0 ? '#ff8a5c' : '#6fd0ff') : '#ff6b6b'
      ctx.fillRect(x, top + barH - fill, 26, fill)

      // Hover line: throttle above it climbs, below it sinks.
      const hy = top + barH - hover * barH
      ctx.strokeStyle = '#ffe066'
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(x - 6, hy)
      ctx.lineTo(x + 32, hy)
      ctx.stroke()

      ctx.fillStyle = '#dbe4f0'
      ctx.font = '600 15px ui-monospace, Consolas, monospace'
      ctx.textAlign = 'center'
      ctx.fillText(i === 0 ? 'L' : 'R', x + 13, top + barH + 19)
      if (!rocket.tracked) {
        ctx.fillStyle = '#ff6b6b'
        ctx.fillText('!', x + 13, top - 8)
      }
    }
    ctx.textAlign = 'left'
  }

  /**
   * Hands-vs-CoM offset. This teaches the one thing that is otherwise
   * invisible and immediately fatal: thrust applied in front of your centre of
   * mass pitches you over, and 25 cm of reach is about a second from
   * unrecoverable. Showing the lever arm turns "it just flipped" into "my
   * hands were out front".
   */
  private drawPosture(ctx: CanvasRenderingContext2D, pilot: Pilot): void {
    const d = pilot.debug.postureOffset
    const bad = d > POSTURE_BAD
    const warn = d > POSTURE_WARN
    if (!warn) return

    ctx.textAlign = 'center'
    ctx.fillStyle = bad ? '#ff6b6b' : '#ffb454'
    ctx.font = '600 17px ui-monospace, Consolas, monospace'
    ctx.fillText(bad ? 'HANDS BACK — ' + (d * 100).toFixed(0) + 'cm' : 'hands forward ' + (d * 100).toFixed(0) + 'cm', W / 2, 200)
    ctx.textAlign = 'left'
  }

  private drawNumbers(
    ctx: CanvasRenderingContext2D,
    pilot: Pilot,
    flight: FirstFlight,
    extra: { preset: string; fps: number },
  ): void {
    const tilt = pilot.tiltDegrees()
    ctx.font = '600 20px ui-monospace, Consolas, monospace'
    ctx.textAlign = 'center'

    ctx.fillStyle = '#8a97ab'
    ctx.font = '13px ui-monospace, Consolas, monospace'
    ctx.fillText('ALT', W / 2 - 96, 226)
    ctx.fillText('SPD', W / 2, 226)
    ctx.fillText('TILT', W / 2 + 96, 226)

    ctx.font = '600 22px ui-monospace, Consolas, monospace'
    ctx.fillStyle = '#dbe4f0'
    ctx.fillText(`${pilot.altitude.toFixed(0)}m`, W / 2 - 96, 250)
    ctx.fillText(`${pilot.body.velocity.length().toFixed(0)}`, W / 2, 250)
    ctx.fillStyle = tilt > 60 ? '#ff6b6b' : tilt > 25 ? '#ffb454' : '#7ee787'
    ctx.fillText(`${tilt.toFixed(0)}°`, W / 2 + 96, 250)

    ctx.font = '13px ui-monospace, Consolas, monospace'
    ctx.fillStyle = '#8a97ab'
    ctx.fillText(
      `${extra.preset}  ·  ${flight.elapsed.toFixed(1)}s  ·  ${flight.crashes} crash  ·  ${extra.fps.toFixed(0)}fps`,
      W / 2,
      278,
    )

    if (flight.lastMessage) {
      ctx.fillStyle = '#ffb454'
      ctx.font = '600 15px ui-monospace, Consolas, monospace'
      ctx.fillText(flight.lastMessage, W / 2, 22)
    }
    ctx.textAlign = 'left'
  }
}

/** Beyond this much forward reach, hover becomes a losing fight. */
const POSTURE_WARN = 0.14
const POSTURE_BAD = 0.24

const _up = new Vector3()
const _fwd = new Vector3()
const _right = new Vector3()
